// Portail patient public (Prompt 7 §6 + correctifs Prompt 10 §C) — page mobile-first, sans compte.
// Route /portail/:token et /t/:token. Appelle /api/portail/{token} (public).
// Dates = plage réelle de la tournée ; disponibilités générées depuis les vrais jours ;
// créneau : première date disponible présélectionnée, aucun créneau coché par défaut.
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Activity, CheckCircle, Hourglass, PhoneCall, Plus, X } from "lucide-react";
import { formatDatesTournee, enumJours, genCreneaux } from "@/lib/tournee";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const frDay = (d) => { if (!d) return ""; const dt = new Date(d + "T00:00:00"); if (isNaN(dt)) return ""; return cap(dt.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })); };

export default function Portail() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const apercu = sp.get("apercu") === "1";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [choix, setChoix] = useState(null); // oui / non / appel
  const [dispo, setDispo] = useState({});
  const [creneau, setCreneau] = useState("");
  const [jourSel, setJourSel] = useState("");
  const [msg, setMsg] = useState("");
  const [invites, setInvites] = useState([]);

  useEffect(() => { axios.get(`${API}/portail/${token}${apercu ? "?apercu=1" : ""}`).then((r) => setInfo(r.data)).catch(() => setErr("Ce lien est invalide ou a expiré.")); }, [token, apercu]);

  // Jours réels de la tournée (plage complète, à partir d'aujourd'hui)
  const jours = useMemo(() => (info ? enumJours(info.jours, info.debut, info.fin) : []), [info]);
  const duree = info?.duree || 60;

  // Première date disponible : premier jour coché en dispo, sinon premier jour de la tournée
  useEffect(() => {
    if (!jours.length) return;
    const cochesJours = jours.filter((d) => dispo[`${d}|Matin`] || dispo[`${d}|Après-midi`]);
    const cible = cochesJours[0] || jours[0];
    if (!jourSel || !jours.includes(jourSel) || (cochesJours.length && !cochesJours.includes(jourSel))) setJourSel(cible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jours, dispo]);

  const toggleDispo = (date, slot) => setDispo((d) => ({ ...d, [`${date}|${slot}`]: !d[`${date}|${slot}`] }));

  const submit = async (desinscription = false) => {
    const disponibilites = Object.entries(dispo).filter(([, v]) => v).map(([k]) => { const [date, plage] = k.split("|"); return { date, plage }; });
    await axios.post(`${API}/portail/${token}/repondre`, {
      veut_soin: choix === "oui", appel: choix === "appel", desinscription,
      disponibilites, creneau, message: msg, invites: invites.filter((i) => i.nom && (i.telephone || i.email)),
    });
    setDone(true);
  };

  if (err) return <Center><div className="text-red-600 text-center">{err}</div></Center>;
  if (!info) return <Center><div className="text-slate-400">Chargement…</div></Center>;
  if (done) return <Center><div className="text-center"><CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-3" /><h2 className="font-display text-xl font-bold text-slate-900">Merci {info.prenom} !</h2><p className="text-slate-500 mt-1 text-sm">Votre réponse a bien été enregistrée. Vous pouvez la modifier avec ce même lien.</p></div></Center>;

  const closed = info.statut === "terminee";
  if (closed) return <Center><div className="text-center"><h2 className="font-display text-xl font-bold text-slate-900">Cette tournée est terminée</h2><p className="text-slate-500 mt-1 text-sm">Contactez directement votre praticien pour un rendez-vous.</p></div></Center>;

  const datesLabel = formatDatesTournee(info.jours?.length ? info.jours : jours);
  const slots = jourSel ? genCreneaux(jourSel) : [];
  const noCreneau = jours.length === 0;

  return (
    <div className="min-h-screen" style={{ background: "#f7f7f5" }}>
      {info.apercu && <div className="w-full text-center py-2 text-[12.5px] font-medium" style={{ background: "#141821", color: "#facc15" }}>Aperçu praticien · aucune réponse ne sera enregistrée</div>}
      <div className="w-full mx-auto p-5" style={{ maxWidth: 480 }}>
        <div className="flex items-center gap-2 mb-4"><div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#facc15" }}><Activity className="w-5 h-5" style={{ color: "#141821" }} /></div><div><div className="font-display font-semibold text-[14px]">Soin xPower</div><div className="text-[12px]" style={{ color: "#6b7180" }}>Ostéopathe à domicile</div></div></div>

        <h1 className="font-display font-bold text-[24px]"><span className="sx-hl">Tournée{info.titre ? ` ${info.titre}` : ""}</span></h1>
        <div className="text-[14px] mt-1.5" style={{ color: "#4a5060" }}>{datesLabel ? `${datesLabel} · à domicile` : "à domicile"}</div>
        <div data-testid="portail-client-nom" className="text-[15px] font-semibold mt-3" style={{ color: "#141821" }}>{[info.prenom, info.nom].filter(Boolean).join(" ")}</div>
        {info.message && <p className="text-[14px] mt-1" style={{ color: "#4a5060" }}>{info.message}</p>}
        {info.date_limite && <div className="flex items-center gap-1.5 text-[13px] mt-3 px-3 py-2 rounded-lg" style={{ background: "oklch(0.96 0.05 80)", color: "oklch(0.45 0.1 75)" }}><Hourglass className="w-4 h-4" />Répondez avant le {frDay(info.date_limite.slice(0, 10))}</div>}

        {/* Choix */}
        <Card title="Souhaitez-vous un rendez-vous ?">
          <div className="grid grid-cols-2 gap-2">
            <Choice testid="portail-oui" active={choix === "oui"} onClick={() => setChoix("oui")} dark>Oui</Choice>
            <Choice testid="portail-non" active={choix === "non"} onClick={() => setChoix("non")}>Non merci</Choice>
          </div>
          <button data-testid="portail-appel" onClick={() => setChoix("appel")} className="w-full mt-2 flex items-center justify-center gap-2 rounded-xl font-medium" style={{ height: 48, border: choix === "appel" ? "2px solid #141821" : "1px solid #d6d8de", background: "#fff" }}><PhoneCall className="w-4 h-4" />J'ai besoin d'être appelé(e)</button>
        </Card>

        {choix === "oui" && !info.figee && (
          <>
            <Card title={`Vos disponibilités${datesLabel ? ` · ${datesLabel}` : ""}`}>
              <div className="space-y-2">
                {jours.map((date) => (
                  <div key={date} className="flex items-center gap-2">
                    <div className="text-[13px] flex-1" style={{ color: "#4a5060" }}>{frDay(date)}</div>
                    {["Matin", "Après-midi"].map((slot) => { const on = dispo[`${date}|${slot}`]; return (
                      <button key={slot} data-testid={`portail-dispo-${date}-${slot}`} onClick={() => toggleDispo(date, slot)} className="rounded-lg text-[13px] px-3" style={{ height: 44, minWidth: 96, background: on ? "#141821" : "#fff", color: on ? "#fff" : "#4a5060", border: "1px solid #d6d8de" }}>{slot}</button>
                    ); })}
                  </div>
                ))}
                {jours.length === 0 && <div className="text-[13px]" style={{ color: "#9199ab" }}>Dates communiquées prochainement.</div>}
              </div>
            </Card>

            <Card title="Choisir un créneau (facultatif)">
              {noCreneau ? (
                <div className="text-[13px] px-3 py-2 rounded-lg" style={{ background: "#f1f1ed", color: "#6b7180" }}>Plus de créneau disponible · votre demande sera placée en liste d'attente.</div>
              ) : (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="portail-jours">
                    {jours.map((d) => (
                      <button key={d} data-testid={`portail-jour-${d}`} onClick={() => { setJourSel(d); setCreneau(""); }} className="shrink-0 rounded-lg px-3 py-2 text-[12.5px]" style={{ background: jourSel === d ? "#141821" : "#fff", color: jourSel === d ? "#fff" : "#4a5060", border: "1px solid #d6d8de" }}>
                        <div className="font-medium">{frDay(d)}</div>
                        <div className="text-[11px] opacity-80">{genCreneaux(d).length} créneaux</div>
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {slots.map((s) => (
                      <button key={s.iso} data-testid={`portail-slot-${s.heure}`} onClick={() => setCreneau(s.iso)} className="rounded-lg text-[13px] mono" style={{ height: 44, background: creneau === s.iso ? "#141821" : "#fff", color: creneau === s.iso ? "#fff" : "#4a5060", border: "1px solid #d6d8de" }}>{s.heure}</button>
                    ))}
                  </div>
                  <div className="text-[12px] mt-2" style={{ color: "#6b7180" }}>Le praticien vous confirme le rendez-vous par SMS.</div>
                </>
              )}
            </Card>
          </>
        )}
        {choix === "oui" && info.figee && <Card title="Tournée complète"><div className="text-[13px]" style={{ color: "#6b7180" }}>Inscrivez-vous sur la liste d'attente : nous vous préviendrons dès qu'un créneau se libère.</div></Card>}

        <Card title="Un mot pour le praticien">
          <textarea data-testid="portail-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Code d'accès, étage…" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: "1px solid #d6d8de" }} />
        </Card>

        <Card title="Inviter un proche">
          <div className="text-[12.5px] mb-2" style={{ color: "#6b7180" }}>Il recevra une invitation pour la même tournée.</div>
          {invites.map((iv, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <input placeholder="Nom" value={iv.nom} onChange={(e) => setInvites((a) => a.map((x, j) => j === i ? { ...x, nom: e.target.value } : x))} className="flex-1 rounded-lg px-2 text-[13px]" style={{ height: 40, border: "1px solid #d6d8de" }} />
              <input placeholder="Téléphone" value={iv.telephone} onChange={(e) => setInvites((a) => a.map((x, j) => j === i ? { ...x, telephone: e.target.value } : x))} className="flex-1 rounded-lg px-2 text-[13px]" style={{ height: 40, border: "1px solid #d6d8de" }} />
              <button onClick={() => setInvites((a) => a.filter((_, j) => j !== i))} className="p-1.5"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button data-testid="portail-invite-add" onClick={() => setInvites((a) => [...a, { nom: "", telephone: "", email: "", accord: false }])} className="inline-flex items-center gap-1 text-[13px] px-3 py-2 rounded-lg" style={{ border: "1px dashed #d6d8de", color: "#6b7180" }}><Plus className="w-4 h-4" />Ajouter un proche</button>
        </Card>

        <button data-testid="portail-submit" disabled={!choix || info.apercu} onClick={() => submit(false)} className="w-full mt-4 rounded-xl font-semibold text-[15px]" style={{ height: 52, background: "#facc15", color: "#141821", opacity: choix && !info.apercu ? 1 : 0.5 }}>Envoyer ma réponse</button>
        <button data-testid="portail-desinscription" disabled={info.apercu} onClick={() => submit(true)} className="w-full mt-3 text-[12px] underline" style={{ color: "#9199ab" }}>Ne plus recevoir de messages de ce praticien</button>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#f7f7f5" }}>{children}</div>;
const Card = ({ title, children }) => <div className="bg-white rounded-2xl p-4 mt-3" style={{ border: "1px solid #ebebe7" }}><div className="font-semibold text-[15px] mb-3" style={{ color: "#141821" }}>{title}</div>{children}</div>;
const Choice = ({ testid, active, onClick, dark, children }) => <button data-testid={testid} onClick={onClick} className="rounded-xl font-medium text-[15px]" style={{ height: 48, background: dark ? (active ? "#141821" : "#fff") : "#fff", color: dark && active ? "#fff" : "#141821", border: active ? "2px solid #141821" : "1px solid #d6d8de" }}>{children}{dark && active ? " ✓" : ""}</button>;
